import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ConnectionService, ConnectionState } from 'ng-connection-service';
import { Subscription, tap } from 'rxjs';

import { AppUtils } from './shared/utils/app.utils';
import { DomSanitizer } from '@angular/platform-browser';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { NoNetwork } from './shared/ui/no-network/no-network';
import { RouterOutlet } from '@angular/router';
import { SignalrWorkerService } from './core/services/signalr/signalr-worker.service';
import { TranslateService } from '@ngx-translate/core';
import { Toast } from './shared/ui/toast/toast';

@Component({
  selector: 'avcc-app-root',
  imports: [RouterOutlet, Toast],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App implements OnInit, OnDestroy {
  /** Service for handling multi-language support and translations. */
  private readonly translate = inject(TranslateService);

  /** Registry to manage and register custom SVG icons for use with MatIcon. */
  private readonly matIconRegistry = inject(MatIconRegistry);

  /** Sanitizer to bypass security for trusted resource URLs (used for loading icons). */
  private readonly domSanitizer = inject(DomSanitizer);

  /** Custom service to monitor online/offline network status. */
  private readonly connectionService = inject(ConnectionService);

  private readonly signalrService = inject(SignalrWorkerService);

  /** Angular Material service to trigger and manage modal dialogs. */
  private readonly dialog = inject(MatDialog);

  /** Subscription container to prevent memory leaks by unsubscribing on destroy. */
  subscription = new Subscription();

  /** Holds the current status of the user's internet/network connection. */
  currentState!: ConnectionState;

  constructor() {
    // Initialize app settings and assets during the construction phase.
    this.setHtmlAttributes();
    this.preloadImages();
    this.addSvgIconsOnload();

    // Define supported locales for the application.
    this.translate.addLangs(['en', 'fr']);
  }

  ngOnInit(): void {
    this.listenNetworkConnection();
  }

  ngOnDestroy(): void {
    // Clean up all active subscriptions.
    this.subscription.unsubscribe();
  }

  /**
   * Monitors real-time network status.
   * Opens a blocking 'No Network' dialog if connection is lost,
   * and automatically closes it when connection is restored.
   */
  listenNetworkConnection() {
    let isDialogOpen = false;
    this.subscription.add(
      this.connectionService
        .monitor()
        .pipe(
          tap((newState: ConnectionState) => {
            const noNetwork = !newState.hasNetworkConnection && !newState.hasInternetAccess;
            this.currentState = newState;

            if (noNetwork) {
              if (!isDialogOpen) {
                this.dialog.open(NoNetwork, {
                  width: 'var(--s-717)',
                  height: 'auto',
                  disableClose: true,
                  panelClass: 'custom-connection-dialog',
                  backdropClass: 'greyscale-backdrop',
                });
                isDialogOpen = true;
              }
            } else {
              // Dismiss the dialog once the network is stable.
              this.dialog.closeAll();
              isDialogOpen = false;
            }
          }),
        )
        .subscribe(),
    );
  }

  /** Sets the global data-theme attribute on the root HTML element for CSS styling. */
  setHtmlAttributes() {
    const html = document.documentElement;
    html.setAttribute('data-theme', 'dark-theme');
  }

  /**
   * Triggers browser caching for key SVG and PNG assets
   * to ensure smooth UI transitions and prevent flickering.
   */
  preloadImages() {
    AppUtils.preloadImages([
      './assets/icons/Operations.svg',
      './assets/icons/YardDog.svg',
      // ... existing asset paths ...
      './assets/icons/gate-ui-icon.svg',
    ]);
  }

  /**
   * Registers custom SVG icons with the MatIconRegistry
   * so they can be used via the <mat-icon svgIcon="..."> component.
   */
  addSvgIconsOnload() {
    this.matIconRegistry.addSvgIcon(
      'shippingyard',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/ShippingYard.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'NotificationIcon',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/gate-notification.svg'),
    );
    this.matIconRegistry.addSvgIcon(
      'delete',
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/delete.svg'),
    );
  }
}
